<template>
  <UApp :locale="uiLocale">
    <NuxtRouteAnnouncer />
    <div class="route-page" :class="{ 'route-page--loading': routeLoading }">
      <NuxtPage />
    </div>
    <Transition name="route-loading-fade">
      <div v-if="routeLoading" class="route-loading" aria-hidden="true">
        <UIcon name="custom:spinner" class="size-7 text-brand-dark-blue animate-spin" />
      </div>
    </Transition>
    <!-- Checkout busy overlay. Rendered here at the app root (a sibling of `.route-page`)
         on purpose: `.route-page` gets `opacity: 0.72` during navigation, which creates a
         stacking context that traps and dims any overlay placed inside the page. At the root
         it is never trapped. Scoped to checkout routes so it never fires on add-to-cart elsewhere. -->
    <div v-if="checkoutBusy" class="fixed inset-0 z-50 bg-white/60 flex items-center justify-center" aria-hidden="true">
      <UIcon name="custom:spinner" class="size-8 text-brand-dark-blue animate-spin" />
    </div>
    <Toaster position="bottom-left" richColors />
  </UApp>
</template>
<script setup lang="ts">
import { Toaster } from 'vue-sonner'
import 'vue-sonner/style.css'
import * as uiLocales from '@nuxt/ui/locale'

const { locale, locales } = useI18n()
const { public: { defaultLocale } } = useRuntimeConfig()
// `page` stays in the canonical url so paginated catalog pages point at themselves
// instead of collapsing onto page 1.
const i18nHead = useLocaleHead({ seo: { canonicalQueries: ['page'] } })
const routeLoading = ref(false)

// Checkout busy overlay (cart review + delivery/payment/confirm). Route names are stable
// across locales (e.g. `checkout-delivery___lt`), so matching on `checkout` covers every
// checkout step without depending on the localized URL segments.
//
// Reads the B2B cart store. The inherited `useCartStore` was the B2C checkout machine —
// a cart-hash cookie, vouchers, deals, delivery and payment steps — and instantiating it
// here kept that whole tree alive on every page of the site long after nothing else
// referenced it.
const route = useRoute()
const cartStore = useB2BCartStore()
// `blocking`, not `loading`: a line edit (stepper, trash) is scoped to its own row in
// B2BCartLine, and covering the whole list for it swallowed clicks on every other row.
const checkoutBusy = computed(() => cartStore.blocking && String(route.name ?? '').includes('checkout'))

if (import.meta.client) {
  const nuxtApp = useNuxtApp()
  let loadingTimer: ReturnType<typeof window.setTimeout> | null = null

  const stopLoading = () => {
    if (loadingTimer) {
      window.clearTimeout(loadingTimer)
      loadingTimer = null
    }
    routeLoading.value = false
  }

  nuxtApp.hook('page:loading:start', () => {
    stopLoading()
    loadingTimer = window.setTimeout(() => {
      routeLoading.value = true
    }, 180)
  })

  nuxtApp.hook('page:loading:end', stopLoading)
  nuxtApp.hook('app:error', stopLoading)
}

// The i18n module strips trailing slashes off the urls it builds for canonical and
// hreflang, while every page actually serves the slashed url (NuxtLink appends one).
// That made canonical point at a url that 301s. Catch-all routes are the mirror
// image — the slash is captured by the `[...slug]` param and can arrive doubled —
// so normalize to exactly one either way. Query and hash are preserved.
const withTrailingSlash = (url: string) => {
  try {
    const parsed = new URL(url)
    parsed.pathname = parsed.pathname.replace(/\/+$/, '') + '/'
    return parsed.toString()
  } catch {
    return url
  }
}

// i18n's own alternates stay on this domain and reuse the current slug in every
// language prefix; useHreflang() replaces them with the CMS's translated, cross-market urls.
const seoLink = computed(() => (i18nHead.value.link ?? [])
  .filter(link => link.rel !== 'alternate')
  .map(link => (
    typeof link.href === 'string' ? { ...link, href: withTrailingSlash(link.href) } : link
  )))

const seoMeta = computed(() => (i18nHead.value.meta ?? []).map(meta => (
  meta.property === 'og:url' && typeof meta.content === 'string'
    ? { ...meta, content: withTrailingSlash(meta.content) }
    : meta
)))

useHead(() => ({
  htmlAttrs: {
    ...i18nHead.value.htmlAttrs,
  },
  link: seoLink.value,
  meta: seoMeta.value,
}))

useHreflang()

const uiLocale = computed(() => {
  const localeCodes = locales.value
    .map(locale => locale.code)
    .filter(code => code !== 'default')

  for (const code of [locale.value, defaultLocale, ...localeCodes]) {
    const normalizedCode = code.toLowerCase().replace('-', '_')
    const uiLocale = uiLocales[normalizedCode as keyof typeof uiLocales]

    if (uiLocale) {
      return uiLocale
    }
  }
})
</script>
<style scoped>
.route-page {
  transition: opacity 180ms ease;
  /* Declaring pointer-events explicitly stops reka's body-level `pointer-events: none`
     (set whenever a modal/slideover/drawer opens) from invalidating this entire subtree.
     The property is inherited, so without a value here every element on the page gets a
     style recalc on the opening click — 4400+ elements on the home page. Every overlay's
     own portal sits above this div and still blocks interaction, so behaviour is unchanged.
     Keep this rule before --loading, which deliberately overrides it during navigation. */
  pointer-events: auto;
}

.route-page--loading {
  opacity: 0.72;
  pointer-events: none;
}

.route-loading {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 9999px;
  background: rgb(255 255 255 / 0.86);
  box-shadow: 0 8px 24px rgb(0 60 114 / 0.14);
  transform: translate(-50%, -50%);
}

.route-loading-fade-enter-active,
.route-loading-fade-leave-active {
  transition: opacity 160ms ease, transform 160ms ease;
}

.route-loading-fade-enter-from,
.route-loading-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, calc(-50% - 4px));
}

@media (prefers-reduced-motion: reduce) {
  .route-page,
  .route-loading-fade-enter-active,
  .route-loading-fade-leave-active {
    transition: none;
  }
}
</style>
